/**
 * Offline Indicator Component
 * Shows connection status and provides offline functionality for the PWA
 */

import { useState, useEffect } from 'react'
import { motion, AnimatePresence } from 'framer-motion'
import { Button } from '@/components/ui/button'
import { cn } from '@/lib/utils'

interface OfflineIndicatorProps {
  className?: string
  showRetry?: boolean
  onRetry?: () => void
}

export function OfflineIndicator({ 
  className, 
  showRetry = true, 
  onRetry 
}: OfflineIndicatorProps) {
  const [, setIsOnline] = useState(navigator.onLine)
  const [showIndicator, setShowIndicator] = useState(!navigator.onLine)
  const [lastSeen, setLastSeen] = useState<Date | null>(null)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      setShowIndicator(false)
      
      // Send message to service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'NETWORK_STATUS_CHANGED',
          payload: { isOnline: true }
        })
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
      setShowIndicator(true)
      setLastSeen(new Date())
      
      // Send message to service worker
      if ('serviceWorker' in navigator && navigator.serviceWorker.controller) {
        navigator.serviceWorker.controller.postMessage({
          type: 'NETWORK_STATUS_CHANGED',
          payload: { isOnline: false }
        })
      }
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [])

  const handleRetry = () => {
    // Force a network check
    const img = new Image()
    img.onload = () => {
      setIsOnline(true)
      setShowIndicator(false)
    }
    img.onerror = () => {
      setIsOnline(false)
      setShowIndicator(true)
    }
    img.src = '/favicon.ico?' + Date.now()
    
    onRetry?.()
  }

  const formatLastSeen = (date: Date) => {
    const now = new Date()
    const diff = now.getTime() - date.getTime()
    const minutes = Math.floor(diff / 60000)
    
    if (minutes < 1) return 'just now'
    if (minutes === 1) return '1 minute ago'
    if (minutes < 60) return `${minutes} minutes ago`
    
    const hours = Math.floor(minutes / 60)
    if (hours === 1) return '1 hour ago'
    if (hours < 24) return `${hours} hours ago`
    
    return date.toLocaleDateString()
  }

  return (
    <AnimatePresence>
      {showIndicator && (
        <motion.div
          initial={{ opacity: 0, y: -50 }}
          animate={{ opacity: 1, y: 0 }}
          exit={{ opacity: 0, y: -50 }}
          transition={{ duration: 0.3 }}
          className={cn(
            'fixed top-4 left-1/2 transform -translate-x-1/2 z-50',
            'bg-gray-900/95 backdrop-blur-sm border border-gray-700 rounded-lg',
            'px-4 py-3 flex items-center gap-3 shadow-lg',
            className
          )}
        >
          <div className="flex items-center gap-2">
            <div className="w-2 h-2 bg-red-500 rounded-full animate-pulse" />
            <span className="text-sm font-medium text-white">
              You're offline
            </span>
          </div>
          
          {lastSeen && (
            <span className="text-xs text-gray-400">
              Last seen {formatLastSeen(lastSeen)}
            </span>
          )}
          
          {showRetry && (
            <Button
              size="sm"
              variant="outline"
              onClick={handleRetry}
              className="ml-2 h-6 px-2 text-xs"
            >
              Retry
            </Button>
          )}
        </motion.div>
      )}
    </AnimatePresence>
  )
}

/**
 * Hook for monitoring online/offline status
 */
export function useOnlineStatus() {
  const [isOnline, setIsOnline] = useState(navigator.onLine)
  const [wasOffline, setWasOffline] = useState(false)

  useEffect(() => {
    const handleOnline = () => {
      setIsOnline(true)
      if (wasOffline) {
        // Trigger data refresh after coming back online
        window.dispatchEvent(new CustomEvent('app:back-online'))
        setWasOffline(false)
      }
    }

    const handleOffline = () => {
      setIsOnline(false)
      setWasOffline(true)
      window.dispatchEvent(new CustomEvent('app:went-offline'))
    }

    window.addEventListener('online', handleOnline)
    window.addEventListener('offline', handleOffline)

    return () => {
      window.removeEventListener('online', handleOnline)
      window.removeEventListener('offline', handleOffline)
    }
  }, [wasOffline])

  return { isOnline, wasOffline }
}

/**
 * Offline-aware fetch wrapper
 */
export async function offlineAwareFetch(
  url: string, 
  options: RequestInit = {}
): Promise<Response> {
  try {
    const response = await fetch(url, {
      ...options,
      // Add cache headers for offline scenarios
      headers: {
        ...options.headers,
        'Cache-Control': 'max-age=300, stale-while-revalidate=86400'
      }
    })
    
    return response
  } catch (error) {
    // If offline, try to get from cache
    if (!navigator.onLine && 'caches' in window) {
      const cache = await caches.open('api-cache')
      const cachedResponse = await cache.match(url)
      
      if (cachedResponse) {
        // Add a header to indicate this is from cache
        const headers = new Headers(cachedResponse.headers)
        headers.set('X-From-Cache', 'true')
        
        return new Response(cachedResponse.body, {
          status: cachedResponse.status,
          statusText: cachedResponse.statusText,
          headers
        })
      }
    }
    
    throw error
  }
}